import type { StudyTimeline } from "@/chat";

/** A timeline with neither a date nor a reason is no timeline (the backend stores it as null). */
export function hasTimeline(timeline: StudyTimeline | null | undefined): timeline is StudyTimeline {
  return timeline != null && (timeline.target_date != null || timeline.reason.trim() !== "");
}

/** "15 Dec 2026" for an ISO date; the raw text if it is not one (never throws on stored data). */
export function formatTimelineDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  // Build in UTC and format in UTC so the calendar day never shifts with the viewer's timezone.
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "Dec 15" for an ISO date, without the year (a board card's deadline badge); the raw text if it
 *  is not one. In UTC, like `formatTimelineDate`, so the day never shifts with the timezone. */
export function formatTimelineMonthDay(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** One line for a timeline: "15 Dec 2026 · IC date", either half alone, or "" when there is none. */
export function formatTimeline(timeline: StudyTimeline | null | undefined): string {
  if (!hasTimeline(timeline)) return "";
  const parts: string[] = [];
  if (timeline.target_date != null) parts.push(formatTimelineDate(timeline.target_date));
  if (timeline.reason.trim() !== "") parts.push(timeline.reason.trim());
  return parts.join(" · ");
}
